import { AuthedButton } from '../authed-button';
import { SpanLoading } from '../fetch-status/span-loading';
import { ButtonProps } from '../ui/button';
import { InteractWatchedIcon, InteractWatchIcon, InteractWatchingIcon } from '@/client/assets';
import { IS_DEBUG } from '@/client/confs/app';
import { cn, getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { refetchSignedFollowMarkets } from '@/client/utils/query-status';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useHoverDirty from 'react-use/lib/useHoverDirty';
import { toast } from 'sonner';

type FollowMarketProps = ButtonProps & {
  isFollowed: boolean;
  address: string;
  iconClassName?: string;
  watchIcon?: ReactNode;
  watchingIcon?: ReactNode;
  watchedIcon?: ReactNode;
};

export const FollowMarket = ({
  address,
  isFollowed,
  iconClassName,
  watchingIcon = <InteractWatchingIcon className={cn('size-4  fill-current', iconClassName)} />,
  watchIcon = <InteractWatchIcon className={cn('size-4 fill-current', iconClassName)} />,
  watchedIcon = <InteractWatchedIcon className={cn('size-4 fill-current', iconClassName)} />,
  ...props
}: FollowMarketProps) => {
  const { t } = useTranslation();
  const [isFollow, setIsFollow] = useState(isFollowed);

  // Sync local state when prop changes (e.g., after refetch or remount)
  useEffect(() => {
    setIsFollow(isFollowed);
  }, [isFollowed]);

  const ref = useRef<HTMLButtonElement>(null);
  const isHover = useHoverDirty(ref);
  const [isLoading, setIsLoading] = useState(false);

  const handleFollow = async () => {
    setIsLoading(true);
    try {
      await api.market.followMarket({}, { marketAddress: address });
      await refetchSignedFollowMarkets();
      setIsFollow(true);
      toast.success(t('followed'));
    } catch (error) {
      if (IS_DEBUG) console.log('followMarket error:', error);
      const msg = getErrorMessage(error);
      toast.error(msg ?? t('follow-failed'));
    }
    setIsLoading(false);
  };
  const handleUnFollow = async () => {
    setIsLoading(true);
    try {
      await api.market.unfollowMarket({}, { marketAddress: address });
      await refetchSignedFollowMarkets();
      setIsFollow(false);
      toast.success(t('unfollowed'));
    } catch (error) {
      if (IS_DEBUG) console.log('unfollowMarket error:', error);
      const msg = getErrorMessage(error);
      toast.error(msg ?? t('unfollow-failed'));
    }
    setIsLoading(false);
  };
  return (
    <AuthedButton
      {...props}
      aria-label={isFollow ? 'Unfollow' : 'Follow'}
      ref={ref}
      showAuth={false}
      showLoading={false}
      variant={props.variant ?? 'outline'}
      size={props.size ?? 'icon'}
      className={cn('rounded-full peer', props.className)}
      disabled={props.disabled || isLoading}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        if (isFollow) handleUnFollow();
        else handleFollow();
        props.onClick?.(e);
      }}
    >
      {isLoading ? (
        <SpanLoading />
      ) : isFollow ? (
        <>{isHover ? watchIcon : watchedIcon}</>
      ) : (
        <>{isHover ? watchingIcon : watchIcon}</>
      )}
    </AuthedButton>
  );
};
